/**
 * Political leaning card (profile side column, screenshots/19 and 20) and "Flag as inaccurate"
 * (screenshots/24 card 3). Always: the AI estimate chip, label + confidence, the range band,
 * "Based on N … · updated …" (leaningBasis), the votes and quotes behind it with watch links,
 * "How this is estimated", and the flag form (owner/admin review queue).
 */
import { useEffect, useRef, useState } from "react";
import { Flag, X } from "lucide-react";
import { AiEstimateChip, LeaningMeter } from "./LeaningMeter";
import { leaningBasis, leaningLabel, type LeaningEstimate } from "./types";
import { Watch, mdy } from "./atoms";
import { peopleApi } from "./peopleStore";
import { useBusy } from "../hooks/useBusy";
import { useFocusTrap } from "../hooks/useFocusTrap";

const CONF = { high: "High confidence", medium: "Medium confidence", low: "Low confidence" } as const;

export function LeaningCard({ personId, estimate, canFlag }: { personId: string; estimate: LeaningEstimate; canFlag: boolean }) {
  const [how, setHow] = useState(false);
  const [flag, setFlag] = useState(false);
  return (
    <section className="p-card p-lean">
      <div className="p-card__head"><h3>Political leaning</h3><AiEstimateChip /></div>
      {estimate.status === "building" ? <LeaningMeter estimate={estimate} /> : <>
        <div>
          <div className="p-lean__label" data-side={leaningLabel(estimate.score).toLowerCase()}>{leaningLabel(estimate.score)}</div>
          <div className="p-lean__conf">{CONF[estimate.confidence]} · {estimate.confidence === "low" ? "wide range, few quotes" : "range shown as the band"}</div>
        </div>
        <LeaningMeter estimate={estimate} />
        <p className="p-lean__basis">{leaningBasis(estimate)} · updated {mdy(estimate.updatedAt)}</p>
        {estimate.votes.length > 0 && <>
          <span className="p-eyebrow">VOTES BEHIND THIS ESTIMATE</span>
          <div className="p-lean__votes">
            {estimate.votes.map((v) => (
              <a key={v.recordId} className="p-lean__vote" href={`/dashboard/legislation/${encodeURIComponent(v.recordId)}`}>
                <span className={`p-vote p-vote--${v.vote}`}>{v.vote.toUpperCase()}</span>
                <span><b>{v.title}</b><small>{v.tally} · {mdy(v.date, { month: "short", day: "numeric", year: "numeric" })}</small></span>
              </a>
            ))}
          </div>
        </>}
        {estimate.evidence.length > 0 && <>
          <span className="p-eyebrow">QUOTES BEHIND THIS ESTIMATE</span>
          {estimate.evidence.map((e) => (
            <blockquote key={`${e.meetingId}-${e.at}`} className="p-quote p-quote--side">
              <p>“{e.quote}”</p>
              <footer>{e.meetingLabel}<Watch meetingId={e.meetingId} at={e.at}>Watch at {clockOf(e.at)}</Watch></footer>
            </blockquote>
          ))}
        </>}
      </>}
      <div className="p-lean__foot">
        <button className="p-link" onClick={() => setHow(true)}>How this is estimated</button>
        {canFlag && <button className="p-flag" onClick={() => setFlag(true)}><Flag size={13} aria-hidden />Flag as inaccurate</button>}
      </div>
      {how && <HowEstimated onClose={() => setHow(false)} />}
      {flag && <FlagForm personId={personId} onClose={() => setFlag(false)} />}
    </section>
  );
}

const clockOf = (s: number) => {
  const t = Math.max(0, Math.floor(s)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

export const REASONS = [["context", "Quote taken out of context"], ["wrong_person", "Wrong person"], ["leaning_off", "Leaning seems off"]] as const;

/** Card 3: "What's wrong with this estimate?" → admin review queue; the person who flagged hears back. */
export function FlagForm({ personId, onClose }: { personId: string; onClose: () => void }) {
  const [reason, setReason] = useState<(typeof REASONS)[number][0]>("context");
  const ref = useRef<HTMLFormElement>(null);
  const { run } = useBusy();
  useEffect(() => { ref.current?.querySelector("input")?.focus(); }, []);
  useFocusTrap(ref, true, { onEscape: onClose });   // Esc closes it; focus stays inside and returns to the opener
  return (
    <div className="p-dialog-backdrop" onClick={onClose}>
      <form ref={ref} className="p-flagform" role="dialog" aria-modal="true" aria-label="Flag as inaccurate" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); void run(() => peopleApi(`${encodeURIComponent(personId)}/flag`, { method: "POST", body: JSON.stringify({ reason }) }).then(onClose, () => {})); }}>
        <button type="button" className="p-dialog-close" aria-label="Close" onClick={onClose}><X size={16} /></button>
        <fieldset>
          <legend>What's wrong with this estimate?</legend>
          {REASONS.map(([id, label]) => (
            <label key={id}><input type="radio" name="reason" checked={reason === id} onChange={() => setReason(id)} />{label}</label>
          ))}
        </fieldset>
        <button className="btn btn--primary" type="submit"><Flag size={13} aria-hidden />Send for review</button>
      </form>
    </div>
  );
}

/** "How leaning is estimated" (page header) / "How this is estimated" (card): the rules from brief §2.6. */
export function HowEstimated({ onClose }: { onClose: () => void }) {
  return (
    <div className="p-dialog-backdrop" onClick={onClose}>
      <div className="p-how" role="dialog" aria-label="How leaning is estimated" onClick={(e) => e.stopPropagation()}>
        <button className="p-dialog-close" aria-label="Close" onClick={onClose}><X size={16} /></button>
        <h3>How leaning is estimated <AiEstimateChip /></h3>
        <ul>
          <li>Officials: divided roll calls and quotes. Votes are the stronger signal.</li>
          <li>Everyone else: quotes from public meetings only.</li>
          <li>Below 20 divided votes or 5 quotes: “Building” instead of a guess.</li>
          <li>Every estimate shows its confidence, its range and the evidence behind it, and can be flagged.</li>
        </ul>
      </div>
    </div>
  );
}
